
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import { getWorkstationOccupancyTimeline } from "../../utils/api";

const getIndiaTime = () =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date());

const getIndiaDate = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const getTimestamp = (value) => {
  if (!value) return null;

  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

const calculateOccupiedSeconds = (timeline, windowStart, windowEnd) => {
  const sessions = Array.isArray(timeline?.sessions)
    ? timeline.sessions
    : [];

  const intervals = sessions
    .map((session) => {
      const start = getTimestamp(
        session.started_at ?? session.start_time ?? session.check_in
      );

      const end = getTimestamp(
        session.ended_at ?? session.end_time ?? session.check_out
      );

      if (start === null) return null;

      const effectiveEnd =
        end ??
        (String(session.status || "").toLowerCase() === "closed"
          ? getTimestamp(session.last_seen_at) ?? start
          : windowEnd);

      const clippedStart = Math.max(start, windowStart);
      const clippedEnd = Math.min(effectiveEnd, windowEnd);

      return clippedEnd > clippedStart
        ? [clippedStart, clippedEnd]
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => a[0] - b[0]);

  // Merge overlapping intervals to avoid double counting.
  const merged = [];

  for (const [start, end] of intervals) {
    const last = merged[merged.length - 1];

    if (last && start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  }

  return merged.reduce(
    (total, [start, end]) => total + (end - start) / 1000,
    0
  );
};

const DeskWiseOccupancyPage = ({ workspaces = [] }) => {
  const [workspaceId, setWorkspaceId] = useState("");
  const [deskData, setDeskData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());

  // Refresh the current time every 30 seconds.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  // Select the first workspace by default.
  useEffect(() => {
  if (workspaces.length === 0) {
    setWorkspaceId("");
    setDeskData([]);
    return;
  }

  setWorkspaceId((previous) => {
    const exists = workspaces.some(
      (workspace) => workspace.id === previous
    );

    if (exists) return previous;

    return workspaces[0].id;
  });
}, [workspaces]);

  const selectedWorkspace = workspaces.find(
    (workspace) => workspace.id === workspaceId
  );

  // Fetch occupancy timeline for each workstation.
  useEffect(() => {
    if (!selectedWorkspace) {
      setDeskData([]);
      setLoading(false);
      setError("");
      return;
    }

    let cancelled = false;

    const loadTimeline = async () => {
      setLoading(true);
      setError("");

      try {
        const indiaDate = getIndiaDate();
        const indiaTime = getIndiaTime();

        const [hours, minutes] = indiaTime.split(":").map(Number);
        const currentMinutes = hours * 60 + minutes;

        const endTime =
          currentMinutes < 9 * 60 ? "09:00" : indiaTime;

        const windowStart = new Date(
          `${indiaDate}T09:00:00+05:30`
        ).getTime();

        const windowEnd = new Date(
          `${indiaDate}T${endTime}:00+05:30`
        ).getTime();

        const totalSeconds = Math.max(
          0,
          (windowEnd - windowStart) / 1000
        );

        const results = await Promise.all(
          (selectedWorkspace.workstations || []).map(async (desk) => {
            const timeline = await getWorkstationOccupancyTimeline({
              cameraId: selectedWorkspace.id,
              workstationId: desk.id,
              date: indiaDate,
              startTime: "09:00",
              endTime,
            });

            const occupiedSeconds = calculateOccupiedSeconds(
              timeline,
              windowStart,
              windowEnd
            );

            const percent =
              totalSeconds > 0
                ? Math.min(
                    100,
                    (occupiedSeconds / totalSeconds) * 100
                  )
                : 0;

            return {
              name: desk.id,
              occupied: percent,
              empty: 100 - percent,
              percent,
              occupiedHours: occupiedSeconds / 3600,
            };
          })
        );

        if (!cancelled) {
          setDeskData(results);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.message || "Failed to load desk occupancy"
          );
          setDeskData([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTimeline();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, now, selectedWorkspace]);

  const chartData = useMemo(
    () =>
      deskData.map((desk, index) => ({
        ...desk,
        label: `Desk ${String(index + 1).padStart(2, "0")}`,
      })),
    [deskData]
  );

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        minWidth: 0,
        height: "100%",
        boxSizing: "border-box",
        border: "1px solid #E2E8F0",
        borderRadius: 3,
        background: "#FFFFFF",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Box sx={{ p: { xs: 2, md: 2.5 } }}>
        {/* Heading and workspace filter inside the card */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 3,
          }}
        >
            <Box
  sx={{
    width: 40,
    height: 40,
    borderRadius: 2,
    backgroundColor: "#EAF2FF",
    color: "#2580F7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }}
>
<BarChartIcon sx={{ color: "#667eea", fontSize: 24 }} />
</Box>
            
              
        
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: 16, sm: 18 },
                fontWeight: 700,
                color: "#0F172A",
              }}
            >
              Desk Wise Occupancy
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Occupied duration from 9:00 AM to current time
            </Typography>
          </Box>

          <FormControl
            size="small"
            sx={{
              width: { xs: "100%", md: 200 },
              flexShrink: 0,
            }}
          >
            <InputLabel id="workspace-select-label">
              Workspace
            </InputLabel>

            <Select
              labelId="workspace-select-label"
              value={workspaceId}
              label="Workspace"
              onChange={(event) => setWorkspaceId(event.target.value)}
            >
              {workspaces.map((workspace) => (
                <MenuItem key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Chart legend */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          {[
            ["#2784F8", "Occupied"],
            ["#D5DDE8", "Empty"],
            ["#EAF3FF", "Occupancy %"],
          ].map(([color, label]) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: color,
                  flexShrink: 0,
                }}
              />

              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Loading state and occupancy chart */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 8,
            }}
          >
            <CircularProgress />
          </Box>
        ) : chartData.length === 0 ? (
          <Typography
            color="text.secondary"
            textAlign="center"
            py={6}
          >
            No workstation data available
          </Typography>
        ) : (
          <Box sx={{ width: "100%", height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 38,
                  right: 10,
                  left: 0,
                  bottom: 25,
                }}
                barCategoryGap="22%"
              >
                <CartesianGrid
                  stroke="#E8EEF7"
                  strokeDasharray="3 3"
                  vertical={false}
                />

                <XAxis
                  dataKey="label"
                  interval={0}
                  tick={{ fill: "#52617D", fontSize: 11 }}
                  tickLine={false}
                />

                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fill: "#52617D", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />

                <Tooltip
                  formatter={(value, name, props) => [
                    name === "occupied"
                      ? `${props.payload.occupiedHours.toFixed(
                          2
                        )} hours (${Number(value).toFixed(1)}%)`
                      : `${Number(value).toFixed(1)}%`,
                    name === "occupied" ? "Occupied" : "Empty",
                  ]}
                />

                <Bar
                  dataKey="occupied"
                  stackId="a"
                  fill="#2784F8"
                  maxBarSize={44}
                />

                <Bar
                  dataKey="empty"
                  stackId="a"
                  fill="#D5DDE8"
                  radius={[5, 5, 0, 0]}
                  maxBarSize={44}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Monitoring window */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
         
        </Typography>
      </Box>
    </Card>
  );
};

export default DeskWiseOccupancyPage;
